import {
  Alert, Badge, Button, Card, Group, Skeleton, Stack, Table, Text, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconRefresh } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  listProvisioningHistory, retryProvisioningRequest,
  type ProvisioningHistoryItem,
} from '@/api/waf'
import { formatApiError } from '@/api/errors'

/**
 * Histórico de pedidos de WAF — todos os tenants, criação e exclusão.
 *
 * Espelha `components/ZtnaAdmin/Dashboard/OnboardingHistoryTable.tsx`, que já
 * resolveu os detalhes desta forma (Table + Badge + Tooltip + `Alert` no vazio).
 *
 * **Por que aqui, numa página com seletor de tenant:** o Registro logo acima já
 * lista WAF de várias contas com o seletor apontando para uma só — 12 linhas de 8
 * contas em 17/08. A página já é entre tenants; o painel só passa a dizer isso no
 * rótulo.
 *
 * Sem polling de propósito: atualização em tempo real está fora da spec, e o que a
 * tela precisa é invalidação após ação — como o Registro já faz.
 */

const STATUS_LABEL: Record<string, string> = {
  applied: 'SUCESSO',
  failed: 'FALHA',
  pending: 'EM ANDAMENTO',
  pending_gmud: 'AGUARDANDO GMUD',
  cancelled: 'CANCELADO',
  blocked: 'SEM CAPACIDADE',
  orphaned: 'REGISTRO REMOVIDO',
}

/** O que o cartao de um `orphaned` PRECISA dizer, e que o rotulo sozinho nao diz:
 *  o pedido terminou, o registro saiu do ConsoleAI e o recurso continua de pe na
 *  conta do cliente. Um rotulo generico seria pior que a ausencia do cartao —
 *  daria a entender que nada aconteceu. */
const ORFAO_EXPLICACAO =
  'Registro removido — Web ACL ainda na AWS. O ConsoleAI perdeu o acesso à conta '
  + 'e não conseguiu apagar o Web ACL. Ele continua existindo e cobrando, e está '
  + 'declarado como órfão no FinOps.'

/** Seis status contra os três badges do ZTNA, e nenhum dobra em outro.
 *  `blocked` fora do vermelho é decisão: capacidade insuficiente não é falha de
 *  execução, e um badge que confunde as duas manda o operador tentar de novo
 *  quando o que falta é IAM na conta do cliente. */
const STATUS_COLOR: Record<string, string> = {
  applied: 'green',
  failed: 'red',
  pending: 'blue',
  pending_gmud: 'blue',
  cancelled: 'gray',
  blocked: 'orange',
  // `yellow` = Warning no DESIGN_SYSTEM.md §2.3, cujo uso listado inclui
  // "alertas de custo" — que e exatamente o que um orfao e. Fora do vermelho de
  // proposito: `failed` convida a tentar de novo e este estado nao tem retry,
  // porque a linha de `managed_wafs` ja foi apagada.
  orphaned: 'yellow',
}

const ACAO_LABEL: Record<string, string> = {
  provision: 'CRIAÇÃO',
  deletion: 'EXCLUSÃO',
}

/** Só estes três. `pending`/`pending_gmud` ainda estão vivos e `applied` não tem o
 *  que refazer — a mesma lista que o backend recusa, escrita aqui para o botão não
 *  aparecer e depois dar 409 na cara de quem clicou. */
const RETENTAVEIS = new Set(['failed', 'cancelled', 'blocked'])

export function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

export function WafProvisioningHistory() {
  const q = useQuery({
    queryKey: ['waf', 'provisioning-history'],
    queryFn: () => listProvisioningHistory(10),
  })
  const linhas: ProvisioningHistoryItem[] = q.data?.items ?? []

  const qc = useQueryClient()
  const retry = useMutation({
    mutationFn: (id: number) => retryProvisioningRequest(id),
    onSuccess: (data) => {
      notifications.show({
        color: 'teal', title: 'Nova tentativa criada',
        message: `Pedido #${data.request_id} registrado.`,
      })
      // Invalidação após ação, não tempo real: a linha nova tem de aparecer sem
      // recarregar a página.
      qc.invalidateQueries({ queryKey: ['waf', 'provisioning-history'] })
      qc.invalidateQueries({ queryKey: ['waf', 'provisioning-requests'] })
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      notifications.show({
        color: 'red', title: 'Não foi possível repetir',
        message: status === 409 && detail === 'retry_exige_gmud'
          ? 'Este tenant exige GMUD: refaça pelo assistente (criação) ou pelo botão Excluir.'
          : status === 409 ? 'Já existe um pedido em andamento equivalente.'
          : status === 403 ? 'Falta a permissão da ação de origem (waf:delete na exclusão).'
          : 'Falha ao criar a nova tentativa.',
      })
    },
  })

  return (
    <Card withBorder radius="md">
      <Group justify="space-between" mb="sm">
        <Text fw={700}>Histórico de pedidos</Text>
        <Text size="xs" c="dimmed">todos os tenants — últimos {linhas.length}</Text>
      </Group>

      {q.isLoading ? (
        <Stack gap="xs">
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Stack>
      ) : q.isError ? (
        <Alert color="red" variant="light">
          {formatApiError(q.error, 'Erro ao ler o histórico de pedidos.')}
        </Alert>
      ) : linhas.length === 0 ? (
        <Alert color="gray" variant="light">Nenhum pedido de WAF registrado ainda.</Alert>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Data</Table.Th>
              <Table.Th>Ação</Table.Th>
              <Table.Th>WAF</Table.Th>
              <Table.Th>Tenant</Table.Th>
              <Table.Th>Usuário</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Duração</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {linhas.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td ff="monospace" fz="xs">
                  {dayjs(row.created_at).format('DD/MM/YYYY HH:mm')}
                </Table.Td>
                <Table.Td>
                  <Badge variant="outline"
                         color={row.kind === 'deletion' ? 'red' : 'teal'}>
                    {ACAO_LABEL[row.kind] ?? 'CRIAÇÃO'}
                  </Badge>
                </Table.Td>
                <Table.Td><Text fw={600} size="sm">{row.desired_name}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {row.tenant_name ?? (row.tenant_id ? `#${row.tenant_id}` : '—')}
                  </Text>
                </Table.Td>
                <Table.Td><Text size="sm">{row.actor_email ?? '—'}</Text></Table.Td>
                <Table.Td>
                  {(() => {
                    // `orphaned` explica SEMPRE — o `error_detail` dele e o motivo
                    // tecnico da falta de acesso, que nao conta o que ficou para
                    // tras. Os dois juntos quando ha motivo.
                    const dica = row.status === 'orphaned'
                      ? [ORFAO_EXPLICACAO, row.error_detail].filter(Boolean).join(' — ')
                      : row.error_detail
                    const selo = (
                      <Badge color={STATUS_COLOR[row.status] ?? 'gray'} variant="light">
                        {STATUS_LABEL[row.status] ?? row.status.toUpperCase()}
                      </Badge>
                    )
                    return dica
                      ? <Tooltip label={dica} multiline w={320}>{selo}</Tooltip>
                      : selo
                  })()}
                </Table.Td>
                <Table.Td ff="monospace" fz="xs">
                  {formatDuration(row.duration_seconds)}
                </Table.Td>
                <Table.Td>
                  {RETENTAVEIS.has(row.status) && (
                    <Button size="xs" variant="default"
                            leftSection={<IconRefresh size={14} />}
                            loading={retry.isPending && retry.variables === row.id}
                            onClick={() => retry.mutate(row.id)}>
                      Tentar de novo
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  )
}

export default WafProvisioningHistory
